import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { APIResponse, API_URL, get, post } from "../api";
import { useAuth } from "../context/AuthContext";

export type AuthenticatorStatus = {
  enabled: boolean;
  enrollment_available: boolean;
  recovery_codes_remaining: number;
};
export type AuthenticatorResult = {
  secret?: string;
  qr_code?: string;
  expires_in?: number;
  recovery_codes?: string[];
  enabled?: boolean;
};
type Action = "setup" | "confirm" | "disable" | "recovery-codes";

export default function useAuthenticator() {
  const { user } = useAuth();
  const client = useQueryClient();
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const key = ["authenticator", user?.id];
  const headers = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
  });
  const status = useQuery<APIResponse<AuthenticatorStatus>, Error>(
    key,
    () =>
      get(`${API_URL}/account/user/settings/authenticator`, {
        headers: headers(),
        cache: "no-store",
      }),
    { enabled: Boolean(user?.id), retry: false },
  );
  const submit = async (action: Action, password: string, code?: string) => {
    if (pending.current)
      throw new Error("Please wait for the current request.");
    pending.current = true;
    setBusy(true);
    try {
      // Do not cache passwords, setup secrets or raw recovery codes in React Query.
      const response = (await post(
        `${API_URL}/account/user/settings/authenticator/${action}`,
        { password, code },
        {
          headers: headers(),
          cache: "no-store",
        },
      )) as APIResponse<AuthenticatorResult>;
      if (!response.data)
        throw new Error(
          "We could not read the response. Refresh security settings before retrying.",
        );
      if (action !== "setup") void client.invalidateQueries(key);
      return response.data;
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };
  return {
    status: status.data?.data,
    loading: status.isLoading,
    statusError: status.error,
    refetch: status.refetch,
    submit,
    busy,
  };
}
